import { useFormik } from "formik";
import ModalComponent from "..";
import { BaseApiErrorResponse, ModalCompProps } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { TextInput } from "../../inputs/textInput";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useAdminTwoFaMutation } from "../../../redux/features/admin/auth/auth.api";
import { InfoComponent } from "../../notification/infoComponent";
import { VerificationSchema } from "../../../utils/validations";
import { useRegenerateBackupCodesMutation } from "../../../redux/features/admin/addAdmin.api";
import { authSliceActions } from "../../../redux/features/auth/auth.slice";

export const EnterVerificationCodeModal = ({
  open,
  handleClose,
  onSuccess,
  handleModalOpen,
}: ModalCompProps & {
  onSuccess: () => void;
  handleModalOpen: (name: string) => void;
}) => {
  const { adminProfile: stateProfile } = useAppSelector((state) => state.auth);
  const [twoFa, { isLoading, isError, error }] = useAdminTwoFaMutation();
  const dispatch = useAppDispatch();

  const [
    regenerateBackUpCodes,
    {
      isLoading: regenerateLoading,
      isError: regenerateIsError,
      error: regenereteError,
    },
  ] = useRegenerateBackupCodesMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    handleChange,
    resetForm,
    isValid,
  } = useFormik({
    initialValues: {
      email: stateProfile?.email,
      twoFaCode: "",
    },
    enableReinitialize: true,
    validationSchema: VerificationSchema,
    onSubmit: async (vals) => {
      const response = await twoFa({
        email: vals.email,
        twoFaCode: vals.twoFaCode,
      });

      if (response?.data?.status === "success") {
        const regenerateResponse = await regenerateBackUpCodes({
          email: vals.email,
          twoFaCode: vals.twoFaCode,
        });
        if (regenerateResponse.data?.status === "success") {
          dispatch(
            authSliceActions.updateAdminBackUpCodes({
              backupCodes: regenerateResponse.data.data.backupCodes,
            }),
          );
          handleModalOpen("backupCodesModal");
          onSuccess();
          resetForm();
        }
      }
    },
  });
  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={"Confirm this action"}
      subtitle={
        "Enter the 6-digit verification code generated by your authenticator app"
      }
      showIcon={true}
      iconType="padlock"
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {(isError || regenerateIsError) && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              (regenereteError as unknown as BaseApiErrorResponse)?.data
                ?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}
        <TextInput
          type="text"
          name="twoFaCode"
          value={values.twoFaCode}
          placeholder="Enter 6-digit Code"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="6-digit Verification Code"
          errors={errors.twoFaCode && touched.twoFaCode ? errors.twoFaCode : ""}
        />

        <div className="flex items-center justify-end gap-2">
          <Button
            text={"Cancel"}
            type="button"
            onClick={() => {
              handleClose();
            }}
            variation="plain-grey"
            className="max-w-fit"
          />
          <Button
            text={"Continue"}
            type="submit"
            className="max-w-fit"
            loading={isLoading || regenerateLoading}
            disabled={!isValid || isLoading || regenerateLoading}
            variation={"primary"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
